import {
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { ARROW_DIRECTIONS, findNeighbor } from './keyboardNav';

export interface RovingItem {
  key: string;
  pairId: number | null;
  x: number;
  y: number;
}

/**
 * Roving tabindex over the points of one pane: the pane is a single tab
 * stop (the selected pair's point, else the last focused point, else the
 * first), and arrow keys move focus spatially between points.
 */
export const useRovingFocus = <C extends Element>(
  items: ReadonlyArray<RovingItem>,
  selectedPairId: number | null
) => {
  const containerRef = useRef<C>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [trackedSelection, setTrackedSelection] = useState(selectedPairId);

  // A new selection (from the table or the map) moves the tab stop to it,
  // unless focus is already on one of that pair's points.
  if (trackedSelection !== selectedPairId) {
    setTrackedSelection(selectedPairId);
    if (selectedPairId !== null) {
      const active = items.find((item) => item.key === activeKey);
      if (active?.pairId !== selectedPairId) {
        setActiveKey(
          items.find((item) => item.pairId === selectedPairId)?.key ?? activeKey
        );
      }
    }
  }

  const tabStop =
    (activeKey !== null && items.some((item) => item.key === activeKey)
      ? activeKey
      : selectedPairId !== null
        ? items.find((item) => item.pairId === selectedPairId)?.key
        : undefined) ??
    items[0]?.key ??
    null;

  const focusKey = (key: string) => {
    const elements =
      containerRef.current?.querySelectorAll<SVGElement | HTMLElement>(
        '[data-point-key]'
      ) ?? [];
    for (const element of elements) {
      if (element.getAttribute('data-point-key') === key) {
        element.focus();
        break;
      }
    }
    setActiveKey(key);
  };

  const onKeyDown = (event: ReactKeyboardEvent, key: string) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const index = items.findIndex((item) => item.key === key);
    if (index < 0) return;
    let target: number | null = null;
    const direction = ARROW_DIRECTIONS[event.key];
    if (direction) target = findNeighbor(items, index, direction);
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = items.length - 1;
    else return;
    event.preventDefault();
    if (target !== null && target !== index) focusKey(items[target].key);
  };

  const onFocus = (key: string) => {
    if (key !== activeKey) setActiveKey(key);
  };

  return { containerRef, tabStop, onKeyDown, onFocus };
};
